// @ts-nocheck
/**
 * Dashboard Table Column Configuration
 * 
 * This file centralizes all table column styling for the Dashboard.
 * Modify the values here to change padding, margin, alignment, and width
 * for both header and body cells across all columns.
 */

// ============================================
// GLOBAL TABLE STYLES
// ============================================

/**
 * Default styles applied to all header cells
 */
export const defaultHeaderStyles = {
  padding: "2px 15px",
  fontSize: "20px",
  color: "#ffffff",
  whiteSpace: "nowrap",
  textAlign: "left",
};

/**
 * Default styles applied to all body cells
 */
export const defaultBodyStyles = {
  paddingTop: "0.5rem",
  paddingBottom: "0.5rem",
  paddingLeft: "0.75rem",
  paddingRight: "0.75rem",
  textAlign: "left",
};

// ============================================
// COLUMN-SPECIFIC CONFIGURATIONS
// ============================================

/**
 * Column configuration object
 * Each key represents a column ID
 * 
 * Properties per column:
 * - headerStyles: Custom styles for the header cell (merged with defaults)
 * - bodyStyles: Custom styles for the body cell (merged with defaults)
 * - sticky: Boolean for sticky positioning
 * - stickyLeft: Left position for sticky columns (e.g., "0", "144px")
 * - zIndexHeader: z-index for sticky header cells
 * - zIndexBody: z-index for sticky body cells
 */
export const columnConfig = {
  eventStatus: {
    headerStyles: {
      minWidth: "10rem",
      textAlign: "left",
    },
    bodyStyles: {
      textAlign: "left",
    },
    sticky: true,
    stickyLeft: "0",
    zIndexHeader: 9,
    zIndexBody: 8,
  },
  eventName: {
    headerStyles: {
      minWidth: "10rem",
      textAlign: "left",
    },
    bodyStyles: {
      textAlign: "left",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "10rem",
    },
    sticky: false,
  },
  venueState: {
    headerStyles: {
      minWidth: "5rem",
      textAlign: "left",
      paddingLeft: "1rem",
    },
    bodyStyles: {
      textAlign: "left",
    },
  },
  venueCity: {
    headerStyles: {
      minWidth: "5rem",
      textAlign: "left",
      paddingLeft: "1rem",
    },
    bodyStyles: {
      textAlign: "left",
    },
  },
  client: {
    headerStyles: {
      minWidth: "5rem",
      textAlign: "left",
    },
    bodyStyles: {
      textAlign: "left",
      textWrap: "nowrap",
    },
  },
  venue: {
    headerStyles: {
      minWidth: "15rem",
      textAlign: "left",
    },
    bodyStyles: {
      textAlign: "left",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "15rem",
    },
  },
  sowStatus: {
    headerStyles: {
      minWidth: "10rem",
      textAlign: "left",
    },
    bodyStyles: {
      textAlign: "left",
    },
  },
  eventStart: {
    headerStyles: {
      minWidth: "10rem",
      textAlign: "left",
    },
    bodyStyles: {
      textAlign: "left",
    },
  },
  eventEnd: {
    headerStyles: {
      minWidth: "10rem",
      textAlign: "left",
    },
    bodyStyles: {
      textAlign: "left",
    },
  },
  daysTillStart: {
    headerStyles: {
      maxWidth: "10rem",
      textAlign: "left",
    },
    bodyStyles: {
      textAlign: "left",
    },
  },
  daysTillEnd: {
    headerStyles: {
      maxWidth: "10rem",
      textAlign: "left",
    },
    bodyStyles: {
      textAlign: "left",
    },
  },
  contact: {
    headerStyles: {
      minWidth: "10rem",
      textAlign: "left",
    },
    bodyStyles: {
      textAlign: "left",
      textWrap: "nowrap",
    },
  },
  clientOnboarding: {
    headerStyles: { minWidth: "12rem", textAlign: "left" },
    bodyStyles: { textAlign: "left" },
  },
  hr: {
    headerStyles: {
      minWidth: "10rem",
      textAlign: "left",
    },
    bodyStyles: {
      textAlign: "left",
    },
  },
  coordinators: {
    headerStyles: {
      minWidth: "10rem",
      textAlign: "left",
    },
    bodyStyles: {
      textAlign: "left",
    },
  },
  notes: {
    headerStyles: {
      minWidth: "10rem",
      textAlign: "left",
    },
    bodyStyles: {
      textAlign: "left",
    },
  },
  estimate: {
    headerStyles: {
      minWidth: "5rem",
      textAlign: "left",
    },
    bodyStyles: {
      textAlign: "left",
      cursor: "pointer",
    },
  },
  clientPo: {
    headerStyles: {
      minWidth: "5rem",
      textAlign: "left",
    },
    bodyStyles: {
      textAlign: "left",
    },
  },
  clientDepositInvoice: {
    headerStyles: {
      minWidth: "5rem",
      textAlign: "left",
    },
    bodyStyles: {
      textAlign: "left",
      cursor: "pointer",
    },
  },
  clientFinalInvoice: {
    headerStyles: {
      minWidth: "5rem",
      textAlign: "left",
    },
    bodyStyles: {
      textAlign: "left",
      cursor: "pointer",
    },
  },
  pl: {
    headerStyles: {
      minWidth: "5rem",
      textAlign: "left",
    },
    bodyStyles: {
      textAlign: "left",
    },
  },
  invoicedAmount: {
    headerStyles: {
      minWidth: "5rem",
      textAlign: "left",
    },
    bodyStyles: {
      textAlign: "left",
    },
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get merged header styles for a specific column
 * @param {string} columnId - The column identifier
 * @returns {object} Merged styles object
 */
export const getHeaderStyles = (columnId, widthOverride) => {
  const columnStyles = columnConfig[columnId]?.headerStyles || {};
  const widthLock =
    typeof widthOverride === "number" && widthOverride > 0
      ? {
          width: `${widthOverride}px`,
          minWidth: `${widthOverride}px`,
          maxWidth: `${widthOverride}px`,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }
      : null;
  return {
    ...defaultHeaderStyles,
    ...columnStyles,
    ...(widthLock || {}),
  };
};

/**
 * Get merged body styles for a specific column
 * @param {string} columnId - The column identifier
 * @param {number} [widthOverride] - Optional pixel width override
 * @returns {object} Merged styles object
 */
export const getBodyStyles = (columnId, widthOverride) => {
  const columnStyles = columnConfig[columnId]?.bodyStyles || {};
  const widthLock =
    typeof widthOverride === "number" && widthOverride > 0
      ? {
          width: `${widthOverride}px`,
          minWidth: `${widthOverride}px`,
          maxWidth: `${widthOverride}px`,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }
      : null;
  return {
    ...defaultBodyStyles,
    ...columnStyles,
    ...(widthLock || {}),
    // Convert to MUI !important format for consistency with existing code
    paddingTop: `${columnStyles.paddingTop || defaultBodyStyles.paddingTop} !important`,
    paddingBottom: `${columnStyles.paddingBottom || defaultBodyStyles.paddingBottom} !important`,
  };
};

/**
 * Get sticky styles for a header cell
 * @param {string} columnId - The column identifier
 * @param {object} options - Options like isEventStatusVisible
 * @returns {object} Sticky styles object or empty object
 */
export const getStickyHeaderStyles = (columnId, options = {}) => {
  const config = columnConfig[columnId];
  if (!config?.sticky) return {};

  const stickyLeft = config.getStickyLeft
    ? config.getStickyLeft(options.isEventStatusVisible, options.eventStatusWidth)
    : config.stickyLeft;

  return {
    position: "sticky",
    left: stickyLeft,
    zIndex: String(config.zIndexHeader || 9),
  };
};

/**
 * Get sticky styles for a body cell
 * @param {string} columnId - The column identifier
 * @param {number} rowIndex - The row index for background color
 * @param {object} options - Options like isEventStatusVisible
 * @returns {object} Sticky styles object or empty object
 */
export const getStickyBodyStyles = (columnId, rowIndex, options = {}) => {
  const config = columnConfig[columnId];
  if (!config?.sticky) return {};

  const stickyLeft = config.getStickyLeftBody
    ? config.getStickyLeftBody(
        options.isEventStatusVisible,
        options.eventStatusWidth,
      )
    : config.stickyLeft;

  return {
    position: "sticky",
    left: stickyLeft,
    zIndex: String(config.zIndexBody || 8),
    backgroundColor: `${
      rowIndex % 2 === 0 ? "rgb(0, 0, 0)" : "rgb(1, 1, 51)"
    } !important`,
  };
};

/**
 * Table row styles configuration
 */
export const tableRowConfig = {
  evenRowBackground: "rgb(0, 0, 0)",
  oddRowBackground: "rgb(1, 1, 51)",
  hoverBackground: "#545454",
  borderColor: "#D175B6",
};

/**
 * Get row background color based on index
 * @param {number} index - Row index
 * @returns {string} Background color
 */
export const getRowBackground = (index) => {
  return index % 2 === 0 
    ? tableRowConfig.evenRowBackground 
    : tableRowConfig.oddRowBackground;
};